import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaChartLine,
  FaCloud,
  FaMobile,
  FaArrowRight,
  FaGoogle,
  FaHashtag,
  FaAd,
  FaCogs,
  FaServer,
  FaDatabase,
  FaPenNib,
  FaEnvelopeOpenText,
  FaChartBar,
  FaCode,
  FaShieldAlt
} from "react-icons/fa";

// =========================================================
// SERVICE CARD (UPDATED HOVER EFFECT ONLY)
// =========================================================

const ServiceCard = ({ title, description, icon, index, isVisible, category, slug }) => {
  const [isHovered, setIsHovered] = useState(false);
  const isDM = category === "digital-marketing";


  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className="w-[290px] h-[335px] cursor-pointer flex justify-center"
    >
      <motion.div
        animate={{
  rotate: isHovered ? 4 : 0,
  y: isHovered ? -10 : 0,
  scale: isHovered ? 1.03 : 1,
  backgroundColor: isHovered
    ? isDM
      ? "#F97316"      // Orange for Digital Marketing
      : "#3b82f6"      // Blue for IT Services
    : "#ffffff",
  boxShadow: isHovered
    ? "0 18px 28px rgba(0,0,0,0.20)"
    : "0 4px 10px rgba(0,0,0,0.10)",
}}

        transition={{ duration: 0.25, ease: "easeOut" }}
        className="w-[280px] h-[320px] rounded-3xl p-5 flex flex-col items-center text-center"
      >
{/* Icon */}
<motion.div
  animate={{
    backgroundColor: isHovered
      ? "#ffffff"
      : isDM
      ? "#F97316"     // Orange for Digital Marketing
      : "#3b82f6",    // Blue for IT Services
    color: isHovered
      ? isDM
        ? "#F97316"
        : "#3b82f6"
      : "#ffffff",
  }}
  className="w-[75px] h-[75px] rounded-2xl flex items-center justify-center mb-4 text-3xl"
>
  {icon}
</motion.div>



        {/* Title */}
        <motion.h3
  animate={{ color: isHovered ? "#ffffff" : "#000000" }}
  className="text-[25px] font-bold leading-tight mb-3 w-[160px]"
  style={{
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  }}
>
  {title}
</motion.h3>

        {/* Description */}
        <motion.p
          animate={{ color: isHovered ? "#fdfdfd" : "#4b5563" }}
          className="text-[15px] font-semibold leading-snug mb-4 px-1 h-[60px]"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {description}
        </motion.p>

        {/* Learn More Button */}
        <motion.button
          onClick={() => {
            const base =
              category === "digital-marketing"
                ? "/services/digital-marketing/"
                : "/services/it/";
            window.location.href = base + slug;
          }}
          animate={{
            backgroundColor: isHovered ? "rgba(255,255,255,0.18)" : "#ffffff",
            color: isHovered ? "#ffffff" : "#000000",
            borderColor: isHovered ? "rgba(255,255,255,0.4)" : "#d1d5db",
          }}
          transition={{ duration: 0.25 }}
          className="border px-4 py-2 rounded-full text-[14px] font-medium flex items-center gap-2"
        >
          Learn More <FaArrowRight className="w-4 h-4" />
        </motion.button>

      </motion.div>
    </motion.div>
  );
};

// Toggle Switch Component
const ServiceToggle = ({ activeCategory, onToggle, isVisible }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.6, delay: 0.6 }}
      className="flex justify-center mb-12"
    >
      <div className="bg-gray-900 bg-opacity-50 border border-gray-700 rounded-2xl p-2 backdrop-blur-sm">
        <div className="flex items-center gap-4">

          {/* 🔸 Digital Marketing Button */}
          <motion.button
            onClick={() => onToggle("digital-marketing")}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`
              relative flex items-center gap-3 px-6 py-3 rounded-xl font-semibold text-lg 
              transition-all duration-300 overflow-hidden
              ${activeCategory === "digital-marketing"
                ? "bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg"
                : "text-gray-300 hover:text-white hover:bg-gray-800"
              }
            `}
          >
            <span className="relative z-10">
              <FaChartLine className="w-5 h-5" />
            </span>
            <span className="relative z-10">Digital Marketing</span>
          </motion.button>

          {/* 🔹 IT Services Button */}
          <motion.button
            onClick={() => onToggle("it-services")}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`
              relative flex items-center gap-3 px-6 py-3 rounded-xl font-semibold text-lg 
              transition-all duration-300 overflow-hidden
              ${activeCategory === "it-services"
                ? "bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg"
                : "text-gray-300 hover:text-white hover:bg-gray-800"
              }
            `}
          >
            <span className="relative z-10">
              <FaCogs className="w-5 h-5" />
            </span>
            <span className="relative z-10">IT Services</span>
          </motion.button>

        </div>
      </div>
    </motion.div>
  );
};





// =========================================================
// MAIN SERVICES COMPONENT
// =========================================================

const Services = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeCategory, setActiveCategory] = useState("digital-marketing");
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, []);

  // SERVICES DATA (unchanged)
  const servicesData = {
  "digital-marketing": [
    {
      title: "SEO Optimization",
      slug: "seo",
      description: "Improve rankings and grow organic traffic.",
      icon: <FaGoogle className="text-white-400" />,
    },
    {
      title: "Social Media Marketing",
      slug: "social-media",
      description: "Boost engagement and brand presence online.",
      icon: <FaHashtag className="text-white-400" />,
    },
    {
      title: "Content Marketing",
      slug: "content",
      description: "Create content that attracts and converts.",
      icon: <FaPenNib className="text-white-400" />,
    },
    {
      title: "PPC Advertising",
      slug: "ppc",
      description: "Get instant results with targeted ads.",
      icon: <FaAd className="text-white-400" />,
    },
    {
      title: "Email Marketing",
      slug: "email",
      description: "Send emails that engage and drive sales.",
      icon: <FaEnvelopeOpenText className="text-white-400" />,
    },
    {
      title: "Analytics & Reporting",
      slug: "analytics",
      description: "Track performance with clear insights.",
      icon: <FaChartBar className="text-white-400" />,
    },
    {
      title: "Conversion Optimization",
      slug: "conversion-optimization",
      description: "Turn visitors into customers effectively.",
      icon: <FaChartLine className="text-white-400" />,
    },
    {
      title: "Influencer Marketing",
      slug: "influencer-marketing",
      description: "Increase reach with trusted influencers.",
      icon: <FaHashtag className="text-white-400" />,
    },
  ],

  "it-services": [
    {
      title: "Web Development",
      slug: "web-development",
      description: "Build fast, modern websites and apps.",
      icon: <FaCode className="text-white-400" />,
    },
    {
      title: "Mobile App Development",
      slug: "mobile-app-development",
      description: "Create smooth apps for iOS and Android.",
      icon: <FaMobile className="text-white-400" />,
    },
    {
      title: "Cloud Solutions",
      slug: "cloud-solutions",
      description: "Deploy secure, scalable cloud systems.",
      icon: <FaCloud className="text-white-400" />,
    },
    {
      title: "Cyber Security",
      slug: "cybersecurity",
      description: "Protect systems from digital threats.",
      icon: <FaShieldAlt className="text-white-400" />,
    },
    {
      title: "IT Infrastructure",
      slug: "infrastructure",
      description: "Set up strong, reliable IT systems.",
      icon: <FaServer className="text-white-400" />,
    },
    {
      title: "Database Management",
      slug: "database",
      description: "Manage and optimize your databases.",
      icon: <FaDatabase className="text-white-400" />,
    },
    {
      title: "DevOps Services",
      slug: "devops",
      description: "Automate workflows for faster delivery.",
      icon: <FaCogs className="text-white-400" />,
    },
    {
      title: "AI & ML",
      slug: "ai-ml",
      description: "Build smart AI-driven applications.",
      icon: <FaServer className="text-white-400" />,
    },
  ],
};

  const currentServices = servicesData[activeCategory] || [];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const handleToggle = (category) => setActiveCategory(category);

  return (
    <section
      ref={sectionRef}
      id="services"
      className="relative pt-5 pb-20 bg-dark text-white overflow-hidden"
    >
      {/* ---------- Background (unchanged) ---------- */}
      <div className="absolute inset-0">
        <div
          className={`absolute top-0 left-0 w-full h-1/2 ${activeCategory === "digital-marketing"
            ? "bg-gradient-to-b from-orange-500/5 to-transparent"
            : "bg-gradient-to-b from-blue-500/5 to-transparent"
            }`}
        />

        <div
          className={`absolute bottom-0 left-0 w-full h-1/2 ${activeCategory === "digital-marketing"
            ? "bg-gradient-to-t from-purple-500/5 to-transparent"
            : "bg-gradient-to-t from-cyan-500/5 to-transparent"
            }`}
        />

        <motion.div
          animate={{ x: [0, 100, 0], y: [0, -50, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className={`absolute top-1/4 left-1/4 w-96 h-96 ${activeCategory === "digital-marketing"
            ? "bg-orange-500/5"
            : "bg-blue-500/5"
            } rounded-full blur-3xl`}
        />

        <motion.div
          animate={{ x: [0, -100, 0], y: [0, 50, 0] }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          className={`absolute bottom-1/4 right-1/4 w-96 h-96 ${activeCategory === "digital-marketing"
            ? "bg-purple-500/5"
            : "bg-cyan-500/5"
            } rounded-full blur-3xl`}
        />
      </div>

     

        <div className="max-w-7xl mx-auto px-4 relative z-10">



        {/* ---------- Header ---------- */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-8"
        >
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-5xl md:text-6xl font-bold mb-6"
          >
            Our{" "}
            <span
              className={`bg-gradient-to-r ${activeCategory === "digital-marketing"
                ? "from-orange-400 to-orange-600"
                : "from-orange-400 to-orange-600"
                } bg-clip-text text-transparent`}
            >
              Services
            </span>
          </motion.h2>

          <motion.div
            initial={{ width: 0 }}
            animate={isVisible ? { width: 96 } : { width: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            className={`h-1 bg-gradient-to-r ${activeCategory === "digital-marketing"
              ? "from-orange-500 to-orange-600"
              : "from-orange-500 to-orange-600"
              } mx-auto mb-8 rounded-full`}
          />

          <motion.p
            initial={{ opacity: 0 }}
            animate={isVisible ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed pb-3"
          >
            {activeCategory === "digital-marketing"
              ? "Transform your digital presence with data-driven marketing strategies that "
              : "Accelerate your business with cutting-edge technology solutions that "}
            <span
              className={`bg-gradient-to-r ${activeCategory === "digital-marketing"
                ? "from-orange-400 to-orange-600"
                : "from-blue-400 to-cyan-600"
                } bg-clip-text text-transparent font-semibold`}
            >
              {activeCategory === "digital-marketing"
                ? "  drive measurable growth"
                : "  scale with your ambition"}
            </span>
            .
          </motion.p>

          {/* Toggle Buttons */}
          <ServiceToggle
            activeCategory={activeCategory}
            onToggle={handleToggle}
            isVisible={isVisible}
          />
        </motion.div>

        {/* ---------- Services Grid ---------- */}
        <motion.div
  variants={containerVariants}
  initial="hidden"
  animate={isVisible ? "visible" : "hidden"}
  className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 justify-items-center"
>

          <AnimatePresence mode="wait">
            {currentServices.map((service, index) => (
              <ServiceCard
                key={`${activeCategory}-${service.title}`}
                title={service.title}
                description={service.description}
                icon={service.icon}
                slug={service.slug}
                index={index}
                isVisible={isVisible}
                category={activeCategory}
              />
            ))}
          </AnimatePresence>
        </motion.div>

        {/* ---------- CTA Button ---------- */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 1 }}
          className="text-center mt-16"
        >
          <motion.button
            onClick={() => (window.location.href = "/contact-us")}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`
              relative bg-gradient-to-r ${activeCategory === "digital-marketing"
                ? "from-orange-500 to-orange-600"
                : "from-blue-500 to-cyan-500"
              } text-white px-8 py-4 rounded-xl font-semibold text-lg 
              transition-all duration-300 hover:shadow-2xl overflow-hidden group
            `}
          >
            <motion.div
              animate={{ x: ["0%", "200%"] }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12"
            />

            <span className="relative z-10 flex items-center gap-2">
              Get {activeCategory === "digital-marketing" ? "Marketing" : "IT"}{" "}
              Consultation
              <motion.div
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <FaArrowRight className="w-4 h-4" />
              </motion.div>
            </span>
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;


